import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";

import type { ISearchEngineDetails } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import { ESearchEngineDevice } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import { deviceEnumToNameMap } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import { getSearchEngineLocationValues } from "app/components/campaigns/searchEngine/SearchEngineWizardSharedUtil";
import type { ICampaignsScrapingConfiguration } from "app/components/campaigns/shared/CampaignsConst";
import { isPerformanceAddOnEnabled } from "app/components/campaigns/shared/utils";
import { apiClient } from "app/modules/api-client/apiClient";
import { fetchAddOnCampaignsMutualAssets } from "app/pages/digital-marketing/brand-monitoring-campaigns/infra/BrandMonitoringCampaignsInfraUtils";
import type {
    ICampaignsConsumptionMetrics,
    IKeywordsCampaignsScrapingKeywords,
    IRTBaseCampaignWithSC,
    IRTCreateCampaign,
    IScarpingWithDetails,
    ITargetUrlIconProps,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { EKeywordsCampaignsWizardPages } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { removeProtocolAndWww } from "app/pages/digital-marketing/utils/url";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import type { IKeyword } from "../shared/types";
import type { ICampaignsWizardState } from "../wizard/campaigns-wizard/types";
import { WebsitePageSearchTypeNumbers } from "../wizard/constants";
import { entitiesArrayToObject } from "../wizard/wizard-pages/create-project-step/web-entities/KeywordsCampaignsWebEntitiesUtils";

import { normalizeCampaignPayload } from "./KeywordsCampaignsPayloadUtils";
import { toAddCampaignRequest } from "./campaignMappers";

export const fetchService = DefaultFetchService.getInstance();

export const getSearchEngineIdByDetails = (searchEngine: ISearchEngineDetails): string => {
    const { location, language, device } = searchEngine;
    return `${location?.text}_${language?.id}_${deviceEnumToNameMap[device]}`;
};

const getSingleSCForAPIRequest = (
    searchEngine: ISearchEngineDetails,
    scrapingConfsWithKeywords: IKeywordsCampaignsScrapingKeywords,
): ICampaignsScrapingConfiguration => {
    const deviceTitle =
        searchEngine.device === ESearchEngineDevice.MOBILE
            ? DEVICES_TYPES_FOR_API.MOBILE
            : DEVICES_TYPES_FOR_API.DESKTOP;
    const scrapingConfigurationName = searchEngine.location.text;
    const searchEngineId = getSearchEngineIdByDetails(searchEngine);
    return {
        searchEngine: searchEngine.company.text,
        geoLocation: scrapingConfigurationName,
        device: deviceTitle,
        locale: searchEngine.language.id,
        geoLocationType: searchEngine.location.type.replaceAll(" ", ""),
        keywords: scrapingConfsWithKeywords[searchEngineId],
        geoLocationCountry: searchEngine.location?.id as number,
    };
};

const getSearchEngineValuesToShow = (searchEngineValues) => {
    return searchEngineValues?.filter((searchEngine) => searchEngine.location);
};

export const manageScrapingConfigurationsWithDetails = (
    searchEngineValues: ISearchEngineDetails[],
    tableDataValue,
    useTags: boolean,
): IScarpingWithDetails => {
    if (!searchEngineValues && !useTags) {
        return { scrapingConfigurations: undefined, tags: undefined, keywordToTargetUrls: undefined };
    }
    const tags: IKeywordsCampaignsScrapingKeywords = {};
    const keywordToTargetUrls: IKeywordsCampaignsScrapingKeywords = {};
    const scrapingConfsWithKeywords = {};
    const searchEngineValuesToShow = getSearchEngineValuesToShow(searchEngineValues);
    searchEngineValuesToShow?.forEach((searchEngine: ISearchEngineDetails) => {
        const searchEngineId = getSearchEngineIdByDetails(searchEngine);
        scrapingConfsWithKeywords[searchEngineId] = [];
    });
    const scrapingConfigurationsSet = new Set(getSearchEngineLocationValues(searchEngineValuesToShow));
    tableDataValue.forEach((keywordProperties) => {
        if (useTags) {
            tags[keywordProperties.keyword] = keywordProperties.tags?.map((tag) => tag.text);
            keywordToTargetUrls[keywordProperties.keyword] = keywordProperties.keywordToTargetUrls;
        }
        keywordProperties.searchEngine?.forEach((searchEngine) => {
            if (searchEngine.isSelected && scrapingConfigurationsSet.has(searchEngine.id)) {
                scrapingConfsWithKeywords[searchEngine.id]?.push(keywordProperties.keyword);
            }
        });
    });
    const scrapingConfigurations: ICampaignsScrapingConfiguration[] = searchEngineValuesToShow.map((scrapingConfig) =>
        getSingleSCForAPIRequest(scrapingConfig, scrapingConfsWithKeywords),
    );
    return { scrapingConfigurations, tags, keywordToTargetUrls };
};

type CampaignScrapingBundle = {
    scrapingConfigurations: ICampaignsScrapingConfiguration[];
    tags: IKeywordsCampaignsScrapingKeywords;
    keywordToTargetUrls: IKeywordsCampaignsScrapingKeywords;
};

export const getScrapingConfigurations = (wizardState: ICampaignsWizardState): ICampaignsScrapingConfiguration[] => {
    const searchEngineValues =
        wizardState.pages[EKeywordsCampaignsWizardPages.SEARCH_ENGINES_STEP].fields.searchEngines.value;
    const keywordsAndTags = wizardState.pages[EKeywordsCampaignsWizardPages.KEYWORDS_STEP].fields.keywordSources.value;
    const keywords: IKeyword[] = keywordsAndTags?.flatMap((source) => source.keywords) ?? [];

    return searchEngineValues.map((searchEngine: ISearchEngineDetails) => {
        return {
            searchEngine: searchEngine.company?.text || "",
            geoLocation: searchEngine.location?.text || "",
            device: deviceEnumToNameMap[searchEngine.device],
            locale: searchEngine.language?.id || "",
            geoLocationType: searchEngine.location?.type.replaceAll(" ", "") || "",
            keywords: keywords.reduce((acc, keyword) => {
                if (keyword.searchEnginesIds.includes(searchEngine.id)) {
                    acc.push(keyword.value);
                }
                return acc;
            }, [] as string[]),
            geoLocationCountry: searchEngine.location?.id as number,
        };
    });
};

const getCampaignScrapingBundle = (wizardState: ICampaignsWizardState): CampaignScrapingBundle => {
    const scrapingConfigurations = getScrapingConfigurations(wizardState);
    const keywordsAndTags = wizardState.pages[EKeywordsCampaignsWizardPages.KEYWORDS_STEP].fields.keywordSources.value;
    const keywords: IKeyword[] = keywordsAndTags?.flatMap((source) => source.keywords) ?? [];

    const { tags, keywordToTargetUrls } = keywords.reduce(
        (acc, keyword) => {
            acc.tags[keyword.value] = keyword.tags || [];
            if (keyword.targetUrl) {
                acc.keywordToTargetUrls[keyword.value] = [keyword.targetUrl].filter(Boolean);
            }
            return acc;
        },
        {
            tags: {} as IKeywordsCampaignsScrapingKeywords,
            keywordToTargetUrls: {} as IKeywordsCampaignsScrapingKeywords,
        },
    );

    return {
        scrapingConfigurations,
        tags,
        keywordToTargetUrls,
    };
};

export const getCampaignData = (campaignName: string, wizardState: ICampaignsWizardState): IRTBaseCampaignWithSC => {
    const createProjectStepFields = wizardState.pages[EKeywordsCampaignsWizardPages.CREATE_PROJECT_STEP].fields;
    const entitiesValue = entitiesArrayToObject(createProjectStepFields.entities.value);
    const searchType = createProjectStepFields.selectedSite?.value?.metadata?.searchType;
    const { scrapingConfigurations, tags, keywordToTargetUrls } = getCampaignScrapingBundle(wizardState);

    return {
        name: campaignName,
        site: createProjectStepFields[wizardState.campaignsFields.SELECTED_SITE].value?.metadata?.id.toLowerCase(),
        competitors: createProjectStepFields[wizardState.campaignsFields.COMPETITORS].value
            .filter((competitor) => competitor?.selected)
            .map((competitor) => competitor.name.toLowerCase()),
        scrapingConfigurations,
        entities: entitiesValue,
        tags,
        keywordToTargetUrls,
        trackingScope: WebsitePageSearchTypeNumbers[searchType],
    };
};

export const saveCampaign = async (campaignData: IRTCreateCampaign) =>
    apiClient.RankTracker.rtAddCampaign(toAddCampaignRequest(normalizeCampaignPayload(campaignData.campaign)));

export const getCampaignsConsumptionFromServer = async (
    currentState: ICampaignsConsumptionMetrics,
): Promise<ICampaignsConsumptionMetrics> => {
    // Check module permissions
    if (!isPerformanceAddOnEnabled()) {
        return {
            ...currentState,
            hasMutualAssetsError: currentState.hasMutualAssetsError || false,
        };
    }
    try {
        const assets = await fetchAddOnCampaignsMutualAssets();
        const { CrossAccountExistingCampaigns, CrossAccountExistingKeywords } = assets;
        return {
            ...currentState,
            usedKeywords: CrossAccountExistingKeywords,
            usedCampaigns: CrossAccountExistingCampaigns,
            hasMutualAssetsError: false,
        };
    } catch (errorFetch) {
        Sentry.error(
            `Error fetching limitations of mutual assets for amount of used campaigns and keywords from server -`,
            errorFetch,
        );
        return {
            ...currentState,
            hasMutualAssetsError: true,
        };
    }
};

const removeSlashFromEndOfUrl = (url: string): string => {
    return url.endsWith("/") ? url.slice(0, -1) : url;
};

const isTargetUrlAndTopUrlMismatch = (topUrlFromTable: string, targetUrl: string[]): boolean => {
    const targetFromTableRemoveSlash = removeSlashFromEndOfUrl(topUrlFromTable);
    return targetUrl.some((url) => {
        const urlRemoveStart = removeProtocolAndWww(url);
        const targetFromRowRemoveSlash = removeSlashFromEndOfUrl(urlRemoveStart);
        return targetFromRowRemoveSlash !== targetFromTableRemoveSlash;
    });
};

export const targetUrlIconColorMapping = {
    default: { buttonIcon: "Install", iconColor: colorsPalettes.carbon[250] } satisfies ITargetUrlIconProps,
    urlMatch: { buttonIcon: "checked-circle", iconColor: colorsPalettes.sky[300] } satisfies ITargetUrlIconProps,
    urlMismatch: { buttonIcon: "alert-circle", iconColor: colorsPalettes.red.s100 } satisfies ITargetUrlIconProps,
};

export const handleTargetUrlButtonIconAndColor = (
    targetUrl: string[],
    topUrlFromTable?: string,
): ITargetUrlIconProps => {
    if (targetUrl) {
        const isEmptyStringInArray = targetUrl.some((url) => url === "");
        const isTargetUrl = targetUrl.length > 0 && !isEmptyStringInArray;

        // 2 conditions: from table or wizard/settings
        // Wizard/Settings - User enter target url or not
        // Table - Also compares the entered target url with the top url from the table
        if (topUrlFromTable && isTargetUrl) {
            const isUrlMismatch = isTargetUrlAndTopUrlMismatch(topUrlFromTable, targetUrl);
            return isUrlMismatch ? targetUrlIconColorMapping.urlMismatch : targetUrlIconColorMapping.urlMatch;
        }
        return isTargetUrl ? targetUrlIconColorMapping.urlMatch : targetUrlIconColorMapping.default;
    } else {
        return targetUrlIconColorMapping.default;
    }
};

/**
 * Extracts the domain from a URL string
 * @param url URL string (can be partial without protocol)
 * @returns domain portion of the URL
 */
const extractDomain = (url: string): string => {
    // Add protocol if missing to make URL parsing work correctly
    const normalizedUrl = url.startsWith("http") ? url : `http://${url}`;

    try {
        const urlObject = new URL(normalizedUrl);
        // Get hostname and remove 'www.' if present
        return urlObject.hostname.replace(/^www\./, "");
    } catch (_error: unknown) {
        // If URL parsing fails, return original string (best effort)
        return removeProtocolAndWww(url).split("/")[0];
    }
};

/**
 * Compares two URLs and determines if they have the same domain
 * @param url1 First URL string (can be partial without protocol)
 * @param url2 Second URL string (can be partial without protocol)
 * @returns boolean indicating if the domains match
 */
export const hasSameDomain = (url1: string, url2: string): boolean => {
    // Handle empty strings
    if (!url1 || !url2) {
        return false;
    }

    // Extract domain from first URL
    const domain1 = extractDomain(url1);

    // Extract domain from second URL
    const domain2 = extractDomain(url2);

    // Compare domains (case insensitive)
    return domain1.toLowerCase() === domain2.toLowerCase();
};

/*
condition 1: targetUrl is empty | not set - Return default icon
condition 2: targetUrl is set and the target domain is similar/identical to the topUrlFromTable - Return checked-circle icon
condition 3: targetUrl is set and the target domain is different to the topUrlFromTable - Return alert-circle icon
condition 4: targetUrl is set and the topUrlFromTable is empty - Return default icon
*/
export const getTargetUrlButtonIconState = (
    targetUrl?: string | string[],
    topUrlFromTable?: string,
): ITargetUrlIconProps => {
    // condition 1: targetUrl is empty | not set - Return default icon
    if (!targetUrl) {
        return targetUrlIconColorMapping.default;
    }

    const incomingTargetUrls = (Array.isArray(targetUrl) ? targetUrl : [targetUrl]).filter(Boolean);

    // condition 1.5: No valid target URLs after filtering - Return default icon
    if (incomingTargetUrls.length === 0) {
        return targetUrlIconColorMapping.default;
    }

    // condition 4: targetUrl is set and the topUrlFromTable is empty - Return default icon
    if (!topUrlFromTable) {
        return targetUrlIconColorMapping.default;
    }

    // condition 2: targetUrl is set and the target domain is similar/identical to the topUrlFromTable - Return checked-circle icon
    // condition 3: targetUrl is set and the target domain is different to the topUrlFromTable - Return alert-circle icon
    const isAnyUrlMatching = incomingTargetUrls.some((url) => hasSameDomain(url, topUrlFromTable));
    return isAnyUrlMatching ? targetUrlIconColorMapping.urlMatch : targetUrlIconColorMapping.urlMismatch;
};

export const onTrackTargetUrlsModalOpen = (isOpen: boolean) => {
    //todo: still no tracking so change the event when given
    if (isOpen) {
        allTrackers.dispatchTrackEvent("Pop Up", "open", "Target URLs modal open");
    }
};
